/**
 * TagsKanbanPage - Drag-and-drop Kanban for managing contact labels.
 *
 * Tela "Funis": cada coluna é uma ETAPA do funil (label no backend), mais a
 * coluna fixa "Sem etapa" na frente e o placeholder "+ Nova etapa" no fim. Cards can be dragged
 * between columns to assign/remove labels. Optimistic updates keep the UI
 * snappy; SSE events keep multiple tabs in sync.
 *
 * Visual language follows the rest of the app — same header pattern as
 * LabelsPage/FollowUpsPage, theme colors via useTheme, lucide icons.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Kanban, Plus, Search,
  Smartphone, Building2, User, Loader2, X, Check, MessageSquare, LogIn,
  Instagram, Facebook, Globe,
  Zap, Settings, GitBranch, UploadCloud, Crown, ChevronLeft, ChevronRight,
  Shuffle, Trophy, XCircle, Timer, SlidersHorizontal, Download,
  MoreHorizontal, Pencil, Palette, Inbox, Users, Trash2, UserCheck,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';

import { useTheme } from '../../contexts/ThemeContext';
import { useSSEEvent, SSE_EVENTS } from '../../contexts/SSEContext';
import authService from '../../services/global/authService';
import companyService from '../../services/global/companyService';
import customFieldsService from '../../services/global/customFieldsService';
import useCustomFieldFilter from '../../hooks/useCustomFieldFilter';
import useFeatureFlags, { getFeatureFlag } from '../../hooks/useFeatureFlags';
import StageAssignModal from '../../components/labels/StageAssignModal';
import kanbanStore from '../../services/kanban/kanbanStore';
import ExportContactsModal from '../../components/labels/ExportContactsModal';
import CustomFieldFilterFields from '../../components/contacts/CustomFieldFilterFields';
import RequiredFieldsBlockModal from '../../components/contacts/RequiredFieldsBlockModal';
import managerDashboardService from '../../services/global/managerDashboardService';
import { useAuth } from '../../contexts/AuthContext';
import { startDelegation } from '../../utils/delegation';
import { navMobileBottom, navMobileTop } from '../../utils/embed';
import { formatBRL, formatBRLCompact } from '../../utils/currency';
import ScheduleMessageModal from '../../components/followups/ScheduleMessageModal';
import StageAutomationsSummary from '../../components/labels/StageAutomationsSummary';
import LabelConfigDrawer from '../../components/labels/LabelConfigDrawer';
import ReplyRulerDrawer from '../../components/labels/ReplyRulerDrawer';
import replyRulerService from '../../services/global/replyRulerService';
import DistributionStatsDrawer from '../../components/labels/DistributionStatsDrawer';
import OutcomeReasonModal from '../../components/labels/OutcomeReasonModal';
import ManualTransferRouletteDrawer from '../../components/labels/ManualTransferRouletteDrawer';
import ContactJourneyDrawer from '../../components/contacts/ContactJourneyDrawer';
import CustomFieldsConfigDrawer from '../../components/contacts/CustomFieldsConfigDrawer';
import ActionMenu, { IconButton } from '../../components/common/ActionMenu';

// Fallback enquanto a API não respondeu — o limite real vem no payload do
// kanban (max_labels), pois varia por plano (business tem teto maior).
const DEFAULT_MAX_LABELS = 20;
const UNLABELED_KEY = 'unlabeled';
const DEFAULT_COLORS = [
  '#25d366', '#00a884', '#10b981', '#22c55e',
  '#84cc16', '#eab308', '#f59e0b', '#f97316',
  '#ef4444', '#dc2626', '#ec4899', '#db2777',
  '#a855f7', '#8b5cf6', '#6366f1', '#3b82f6',
  '#0ea5e9', '#06b6d4', '#14b8a6', '#64748b',
];
const pickColor = (i) => DEFAULT_COLORS[i % DEFAULT_COLORS.length];

// ----------------------------------------------------------------------------
// Card
// ----------------------------------------------------------------------------
// Multi-funil (flag multi_funnel): os outros funis em que o card está, com a
// etapa de cada um. `stageCtx` = { multi, catalogById, funnelsById,
// activeFunnelId } vem da página; sem a flag devolve vazio (nada muda).
const otherStagesOf = (contact, stageCtx) => {
  if (!stageCtx || !stageCtx.multi) return [];
  const seen = new Set();
  const out = [];
  for (const id of (contact.label_ids || [])) {
    const label = stageCtx.catalogById?.[id];
    if (!label) continue;
    const fid = label.pipeline_id || '';
    if (fid === (stageCtx.activeFunnelId || '') || seen.has(fid)) continue;
    seen.add(fid);
    out.push({ label, funnelName: stageCtx.funnelsById?.[fid]?.name || 'Funil' });
  }
  return out;
};

// Chips de canal do cartao (lead unificado): mesma paleta do ChannelBadge.
const CANAL_DO_CARTAO = {
  wa_personal: { label: 'WhatsApp', Icon: Smartphone, color: '#25d366' },
  wa_business: { label: 'WhatsApp Business', Icon: Building2, color: '#00a884' },
  instagram: { label: 'Instagram', Icon: Instagram, color: '#c13584' },
  messenger: { label: 'Messenger', Icon: Facebook, color: '#1877f2' },
  widget: { label: 'Widget', Icon: Globe, color: '#00a884' },
};

const ContactCard = ({ contact, colors, isOverlay, onOpenChat, onOpenJourney, originName, stageCtx }) => {
  // Paridade D7 (2026-09-16): Instagram e Messenger tinham o icone do QR e a
  // linha de preview caia em "+null" (nao tem telefone).
  const channelIcon =
    contact.channel === 'wa_business' ? <Building2 size={12} /> :
    contact.channel === 'instagram' ? <Instagram size={12} /> :
    contact.channel === 'messenger' ? <Facebook size={12} /> :
    contact.channel === 'widget' ? <Globe size={12} /> :
    contact.channel === 'both' ? (
      <span style={{ display: 'inline-flex', gap: 2 }}>
        <Smartphone size={12} /> <Building2 size={12} />
      </span>
    ) : <Smartphone size={12} />;
  const previewFallback = contact.phone ? `+${contact.phone}` : (contact.contact_ref || '');
  const otherStages = otherStagesOf(contact, stageCtx);
  // Lead unificado nos 3 canais (flag lead_multichannel no backend): o cartao
  // traz `channels` e `identities` quando a MESMA pessoa conversa por mais de
  // um canal. Um chip por canal abre a conversa daquele canal (o handler dos
  // 4 shells ja roteia por contact_ref/phone e channel).
  const canaisDoLead = Array.isArray(contact.channels) && contact.channels.length > 1 ? contact.channels : [];
  const abrirNoCanal = (ch) => {
    const idn = (contact.identities || []).find((i) => i && i.channel === ch) || null;
    const key = String((idn && idn.key) || '');
    const opaca = key.startsWith('instagram:') || key.startsWith('messenger:') || key.startsWith('widget:');
    onOpenChat?.({
      ...contact,
      channel: ch,
      phone: opaca ? null : (key || contact.phone || null),
      contact_ref: opaca ? key : null,
    });
  };

  return (
    <div
      style={{
        backgroundColor: colors.bgPrimary,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: 10,
        padding: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        cursor: 'grab',
        boxShadow: isOverlay ? '0 8px 24px rgba(0,0,0,0.20)' : 'none',
        transform: isOverlay ? 'scale(1.02)' : 'none',
        transition: 'box-shadow 150ms, transform 150ms',
        userSelect: 'none',
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        backgroundColor: colors.bgTertiary,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', flexShrink: 0,
      }}>
        {contact.profile_pic_url ? (
          // eslint-disable-next-line jsx-a11y/alt-text
          <img
            src={contact.profile_pic_url}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <User size={18} color={colors.textTertiary} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 14, fontWeight: 500, color: colors.textPrimary,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {contact.display_name || contact.phone}
        </div>
        {(contact.assigned_to?.name || originName) && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 1,
            fontSize: 11, fontWeight: 600, color: '#d97706',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
          }}>
            {/* Responsável do contato (assigned_to) tem prioridade; cai no dono
                do workspace (origin) quando não há responsável atribuído. */}
            de {contact.assigned_to?.name || originName}
          </div>
        )}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 12, color: colors.textSecondary,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          <span style={{ color: colors.textTertiary, display: 'inline-flex' }}>
            {channelIcon}
          </span>
          <span style={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {contact.last_message_preview || previewFallback}
          </span>
        </div>
        {canaisDoLead.length > 0 && !isOverlay && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }} title="Esta pessoa conversa por mais de um canal">
            {canaisDoLead.map((ch) => {
              const meta = CANAL_DO_CARTAO[ch] || CANAL_DO_CARTAO.wa_personal;
              const Icon = meta.Icon;
              return (
                <button
                  key={ch}
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); abrirNoCanal(ch); }}
                  title={`Abrir no ${meta.label}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 10, fontWeight: 700, lineHeight: '16px', padding: '0 6px',
                    borderRadius: 999, border: `1px solid ${meta.color}55`,
                    backgroundColor: `${meta.color}14`, color: meta.color,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  <Icon size={10} /> {meta.label}
                </button>
              );
            })}
          </div>
        )}
        {otherStages.length > 0 && (
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}
            title={`Também em: ${otherStages.map((s) => `${s.funnelName} · ${s.label.name}`).join(', ')}`}
          >
            {otherStages.map(({ label, funnelName }) => (
              <span key={label.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 10, fontWeight: 600, lineHeight: '16px',
                padding: '0 6px', borderRadius: 4, maxWidth: '100%', minWidth: 0,
                backgroundColor: colors.bgSecondary, color: colors.textSecondary,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: label.color, flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {funnelName} · {label.name}
                </span>
              </span>
            ))}
          </div>
        )}
        {contact.value !== null && contact.value !== undefined && contact.value !== '' && (
          <div style={{
            marginTop: 2, fontSize: 11, fontWeight: 600, color: '#059669',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {formatBRL(contact.value)}
          </div>
        )}
      </div>
      {!isOverlay && onOpenJourney && (
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onOpenJourney(contact);
          }}
          title="Ver jornada do contato"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, border: 'none', background: 'transparent',
            color: colors.textSecondary, borderRadius: 8,
            cursor: 'pointer', flexShrink: 0,
            transition: 'background-color 150ms, color 150ms',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.bgSecondary;
            e.currentTarget.style.color = colors.accentPrimary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = colors.textSecondary;
          }}
        >
          <GitBranch size={14} />
        </button>
      )}
      {onOpenChat && !isOverlay && (
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onOpenChat(contact);
          }}
          title={originName ? `Entrar no workspace de ${originName}` : 'Abrir conversa'}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, border: 'none', background: 'transparent',
            color: colors.textSecondary, borderRadius: 8,
            cursor: 'pointer', flexShrink: 0,
            transition: 'background-color 150ms, color 150ms',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.bgSecondary;
            e.currentTarget.style.color = colors.accentPrimary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = colors.textSecondary;
          }}
        >
          {originName ? <LogIn size={16} /> : <MessageSquare size={16} />}
        </button>
      )}
    </div>
  );
};

// Chave channel-agnostica do card: WhatsApp usa `phone`, Instagram usa
// `contact_ref` ('instagram:{igsid}') pois nao tem telefone. E o que viaja de
// volta ao assign/remove de etiqueta — sem isso o card de IG mandava `phone`
// nulo e a etiqueta NAO grudava (voltava pra "sem etiqueta" ao recarregar).
const cardKey = (c) => (c && (c.contact_ref || c.phone)) || '';

const DraggableCard = ({ contact, sourceColumnId, colors, onOpenChat, onOpenJourney, originName, stageCtx }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${sourceColumnId}::${cardKey(contact)}`,
    data: { contact, sourceColumnId },
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ opacity: isDragging ? 0 : 1 }}
    >
      <ContactCard
        contact={contact}
        colors={colors}
        onOpenChat={onOpenChat}
        onOpenJourney={onOpenJourney}
        originName={originName}
        stageCtx={stageCtx}
      />
    </div>
  );
};

// ----------------------------------------------------------------------------
// Column
// ----------------------------------------------------------------------------
const KanbanColumn = ({
  id, title, color, count, contacts, colors, onRename, onDelete, isUnlabeled, onOpenChat, onChangeColor,
  // Fase 1+2: campaign dispatch and per-label config (auto_dispatch + rules).
  onDispatch, onConfig,
  // Atribuir responsável (membro ou agente) a todos os contatos da coluna.
  onAssignAll,
  // PR-B: open per-contact journey drawer.
  onOpenJourney,
  // Reordenar colunas (setas no header).
  canMoveLeft, canMoveRight, onMove,
  // Fase 4 (item B): membro com funis espelhados não gerencia definições
  // (readOnly); cards de outros membros (origin != currentUserId) abrem por
  // delegação (onEnterForeign) em vez de arrastar.
  readOnly, currentUserId, onEnterForeign,
  // Multi-funil: contexto para o card mostrar os outros funis em que está.
  stageCtx,
  // Funcionalidade A: o ADMIN vê tudo no PRÓPRIO workspace (admin_mirror/
  // cartão herdado) — card alheio abre o chat normal, sem delegação.
  viewerIsAdmin,
  // Paginação SERVER-SIDE: has_more do servidor + carregar a próxima página.
  hasMore, onLoadMore,
  // Busca por lead dentro da etapa (server-side, precisa do funil ativo).
  funnelId,
  // Resultado da etapa ('won' | 'lost' | null) — selo no header. O nome da
  // coluna é livre; quem diz o que é ganho/perda para os relatórios é isto.
  outcome,
  // Total do campo Valor da coluna (soma server-side, independente da página).
  valueTotal,
  // Largura da coluna (mobile encolhe para caber uma coluna por tela).
  width = 300,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState(title);
  const [showColorPicker, setShowColorPicker] = useState(false);
  // Paginação SERVER-SIDE: a coluna renderiza os cards JÁ carregados; o
  // "Carregar mais" busca a próxima página no servidor (não fatia no cliente).
  const [loadingMore, setLoadingMore] = useState(false);
  // Busca por lead DENTRO da etapa (server-side — a coluna pode ter milhares de
  // contatos paginados, então filtrar só a página carregada enganaria). >= 2
  // chars busca; vazio volta ao normal.
  const [colQ, setColQ] = useState('');
  const [colRes, setColRes] = useState(null);
  const [colLoading, setColLoading] = useState(false);
  useEffect(() => {
    const term = colQ.trim();
    if (term.length < 2) { setColRes(null); setColLoading(false); return undefined; }
    let alive = true;
    setColLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await authService.getKanbanLabelPage(id, funnelId, 0, 50, term);
        const data = res?.data || res;
        if (alive) setColRes(data?.contacts || []);
      } catch (e) {
        if (alive) setColRes([]);
      } finally {
        if (alive) setColLoading(false);
      }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [colQ, id, funnelId]);

  useEffect(() => { setDraftName(title); }, [title]);

  const searching = colQ.trim().length >= 2;
  const visibleContacts = searching && colRes !== null ? colRes : contacts;

  // Close color picker when clicking outside
  useEffect(() => {
    if (!showColorPicker) return;
    const onDocClick = (e) => {
      if (!e.target.closest(`[data-color-picker="${id}"]`)) {
        setShowColorPicker(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showColorPicker, id]);

  const headerBg = isOver ? `${color}22` : colors.bgPrimary;

  // Ações da coluna: tudo que era uma fileira de ícones no header (setas,
  // raio, engrenagem, X) vive num único menu "⋯". O header fica só com o que
  // o operador lê de relance: cor, nome, resultado, contagem e valor.
  const menuItems = [];
  if (!readOnly) {
    if (isUnlabeled) {
      if (onConfig) {
        menuItems.push({
          key: 'entry', label: 'Automação de entrada', icon: Inbox, onClick: onConfig,
          hint: 'O que acontece com um contato novo no funil',
        });
      }
      if (onAssignAll) {
        menuItems.push({
          key: 'assign', label: 'Atribuir responsável', icon: UserCheck, onClick: onAssignAll,
          disabled: count === 0,
          hint: count === 0 ? 'A coluna está vazia' : `Membro ou agente para os ${count} contatos sem etapa`,
        });
      }
    } else {
      menuItems.push({ key: 'rename', label: 'Renomear etapa', icon: Pencil, onClick: () => setRenaming(true) });
      menuItems.push({ key: 'color', label: 'Mudar cor', icon: Palette, onClick: () => setShowColorPicker(true) });
      if (onConfig) {
        menuItems.push({
          key: 'config', label: 'Automações da etapa', icon: Settings, onClick: onConfig,
          hint: 'Sequência, regras, tags e responsável',
        });
      }
      if (onDispatch) {
        // Disparar para etapa vazia criaria um follow-up inútil (o scheduler
        // registra como falha): o item fica desabilitado com o motivo.
        menuItems.push({
          key: 'dispatch', label: 'Disparar campanha', icon: Zap, onClick: onDispatch,
          disabled: count === 0,
          hint: count === 0 ? 'A etapa está vazia' : `Para os ${count} contatos desta etapa`,
        });
      }
      if (onAssignAll) {
        menuItems.push({
          key: 'assign', label: 'Atribuir responsável', icon: UserCheck, onClick: onAssignAll,
          disabled: count === 0,
          hint: count === 0 ? 'A etapa está vazia' : `Membro ou agente para os ${count} contatos`,
        });
      }
      if (onMove) {
        menuItems.push({ divider: true });
        menuItems.push({ key: 'left', label: 'Mover para a esquerda', icon: ChevronLeft, disabled: !canMoveLeft, onClick: () => onMove(-1) });
        menuItems.push({ key: 'right', label: 'Mover para a direita', icon: ChevronRight, disabled: !canMoveRight, onClick: () => onMove(1) });
      }
      if (onDelete) {
        menuItems.push({ divider: true });
        menuItems.push({ key: 'delete', label: 'Excluir etapa', icon: Trash2, danger: true, onClick: onDelete });
      }
    }
  } else {
    // Membro com funis compartilhados (2026-10-14, decisão do dono): usa a
    // etapa sem editar. Estrutura e automações continuam só com o admin (o
    // backend recusa); o membro vê o que a etapa faz, dispara para a própria
    // carteira e atribui responsável.
    if (!isUnlabeled && onConfig) {
      menuItems.push({
        key: 'config', label: 'Automações da etapa', icon: Settings, onClick: onConfig,
        hint: 'Somente leitura: configuradas pelo administrador',
      });
    }
    if (!isUnlabeled && onDispatch) {
      menuItems.push({
        key: 'dispatch', label: 'Disparar campanha', icon: Zap, onClick: onDispatch,
        disabled: count === 0,
        hint: count === 0 ? 'A etapa está vazia' : 'Para os contatos da sua carteira nesta etapa',
      });
    }
    if (onAssignAll) {
      menuItems.push({
        key: 'assign', label: 'Atribuir responsável', icon: UserCheck, onClick: onAssignAll,
        disabled: count === 0,
        hint: count === 0
          ? (isUnlabeled ? 'A coluna está vazia' : 'A etapa está vazia')
          : `Membro ou agente para os ${count} contatos`,
      });
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        width, flexShrink: 0,
        backgroundColor: colors.bgPrimary,
        border: `1px solid ${isOver ? color : colors.borderColor}`,
        borderRadius: 12,
        display: 'flex', flexDirection: 'column',
        maxHeight: '100%',
        scrollSnapAlign: 'start',
        transition: 'border-color 150ms, background-color 150ms',
      }}
    >
      {/* Column header: cor + nome + resultado + contadores + menu de ações. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 8px 10px 14px', backgroundColor: headerBg,
        borderBottom: `1px solid ${colors.borderColor}`,
        borderTopLeftRadius: 12, borderTopRightRadius: 12,
        transition: 'background-color 150ms',
      }}>
        <div data-color-picker={id} style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
          <button
            onClick={() => !isUnlabeled && !readOnly && setShowColorPicker((v) => !v)}
            disabled={isUnlabeled || readOnly}
            title={isUnlabeled || readOnly ? '' : 'Mudar cor'}
            style={{
              width: 12, height: 12, borderRadius: 4, backgroundColor: color,
              border: 'none', padding: 0,
              cursor: isUnlabeled || readOnly ? 'default' : 'pointer',
              boxShadow: showColorPicker ? `0 0 0 2px ${colors.accentPrimary}66` : 'none',
              transition: 'box-shadow 150ms',
            }}
          />
          {showColorPicker && !isUnlabeled && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', left: -6,
              backgroundColor: colors.bgPrimary,
              border: `1px solid ${colors.borderColor}`,
              borderRadius: 10, padding: 8, zIndex: 200,
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              display: 'grid', gridTemplateColumns: 'repeat(5, 22px)', gap: 6,
            }}>
              {DEFAULT_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => {
                    setShowColorPicker(false);
                    if (c !== color) onChangeColor?.(c);
                  }}
                  title={c}
                  style={{
                    width: 22, height: 22, borderRadius: 6,
                    backgroundColor: c, border: 'none', cursor: 'pointer',
                    outline: c === color ? `2px solid ${colors.textPrimary}` : 'none',
                    outlineOffset: 1,
                  }}
                />
              ))}
            </div>
          )}
        </div>
        {renaming && !isUnlabeled ? (
          <input
            autoFocus
            value={draftName}
            maxLength={50}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={() => {
              const next = draftName.trim();
              if (next && next !== title) onRename?.(next);
              setRenaming(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') { setDraftName(title); setRenaming(false); }
            }}
            style={{
              flex: 1, minWidth: 0, padding: '4px 6px', borderRadius: 6,
              border: `1px solid ${colors.accentPrimary}`,
              backgroundColor: colors.bgSecondary,
              color: colors.textPrimary, fontSize: 14, outline: 'none',
            }}
          />
        ) : (
          <span
            onDoubleClick={() => !isUnlabeled && !readOnly && setRenaming(true)}
            style={{
              flex: 1, minWidth: 0,
              fontSize: 14, fontWeight: 600, color: colors.textPrimary,
              cursor: isUnlabeled || readOnly ? 'default' : 'text',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
            title={isUnlabeled || readOnly ? title : 'Duplo clique para renomear'}
          >
            {title}
          </span>
        )}
        {/* Selo de resultado: diz de relance que esta coluna fecha o funil. */}
        {(outcome === 'won' || outcome === 'lost') && (
          <span
            title={outcome === 'won'
              ? 'Etapa de ganho: conta como fechamento nos relatórios'
              : 'Etapa de perda: conta como perda nos relatórios'}
            style={{
              display: 'flex', alignItems: 'center', flexShrink: 0,
              color: outcome === 'won' ? colors.accentPrimary : colors.danger,
            }}
          >
            {outcome === 'won' ? <Trophy size={14} /> : <XCircle size={14} />}
          </span>
        )}
        <span style={{
          fontSize: 12, fontWeight: 600, color: colors.textSecondary,
          backgroundColor: colors.bgSecondary, flexShrink: 0,
          padding: '2px 8px', borderRadius: 999,
        }}>
          {count}
        </span>
        {/* Total do campo Valor da coluna inteira (server-side, não depende
            da paginação). Só aparece quando a etapa tem algum valor somado. */}
        {valueTotal > 0 && (
          <span
            title={`Valor total da etapa: ${formatBRL(valueTotal)}`}
            style={{
              fontSize: 11, fontWeight: 600, color: '#059669',
              backgroundColor: '#05966912', flexShrink: 0,
              padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
            }}
          >
            {formatBRLCompact(valueTotal)}
          </span>
        )}
        {menuItems.length > 0 && (
          <ActionMenu
            colors={colors}
            items={menuItems}
            title={title}
            trigger={({ open }) => (
              <IconButton icon={MoreHorizontal} label="Ações da etapa" colors={colors} size={28} iconSize={16} active={open} />
            )}
          />
        )}
      </div>

      {/* Busca por lead DENTRO da etapa (server-side). */}
      <div style={{ padding: '8px 10px 0', flexShrink: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          backgroundColor: colors.bgTertiary, borderRadius: 8,
          padding: '0 8px', height: 32,
        }}>
          <Search size={13} color={colors.textTertiary} style={{ flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Buscar nesta etapa..."
            value={colQ}
            onChange={(e) => setColQ(e.target.value)}
            style={{
              flex: 1, minWidth: 0, border: 'none', background: 'transparent',
              fontSize: 12, color: colors.textPrimary, outline: 'none', padding: '6px 2px',
            }}
          />
          {colLoading && (
            <Loader2 size={12} style={{ animation: 'spin 1s linear infinite', color: colors.textTertiary, flexShrink: 0 }} />
          )}
          {colQ && !colLoading && (
            <X size={13} color={colors.textTertiary} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => setColQ('')} />
          )}
        </div>
      </div>

      {/* Column body */}
      <div style={{
        flex: 1, padding: 10, overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 8,
        minHeight: 80,
      }}>
        {visibleContacts.length === 0 ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: colors.textTertiary, fontSize: 12, padding: 24,
            border: `1px dashed ${colors.borderColor}`, borderRadius: 8,
            textAlign: 'center', minHeight: 60,
          }}>
            {searching ? 'Nenhum lead encontrado nesta etapa.' : 'Arraste contatos para cá'}
          </div>
        ) : (
          <>
            {visibleContacts.map((c) => {
              const foreign = c.origin && c.origin.user_id && c.origin.user_id !== currentUserId;
              // Foreign (visão "all" do admin ou membro com acesso ao funil): o
              // card É ARRASTÁVEL — mover entre colunas grava na partição do
              // DONO (o servidor revalida a permissão).
              // ADMIN: vê o contato no PRÓPRIO workspace (admin_mirror/cartão
              // herdado) → ícone normal "Abrir conversa" (onOpenChat), SEM
              // delegação. MEMBRO com acesso ao funil: entra no workspace do
              // dono (onEnterForeign, validado no servidor) com a label do dono.
              const delegateForeign = foreign && !viewerIsAdmin;
              return (
                <DraggableCard
                  key={`${id}:${cardKey(c)}`}
                  contact={c}
                  sourceColumnId={id}
                  colors={colors}
                  onOpenChat={delegateForeign ? onEnterForeign : onOpenChat}
                  onOpenJourney={onOpenJourney}
                  originName={delegateForeign ? c.origin?.name : undefined}
                  stageCtx={stageCtx}
                />
              );
            })}
            {hasMore && !searching && (
              <button
                onClick={async () => {
                  setLoadingMore(true);
                  try { await onLoadMore?.(id); } finally { setLoadingMore(false); }
                }}
                disabled={loadingMore}
                style={{
                  marginTop: 4, padding: '8px 10px', borderRadius: 8,
                  border: `1px dashed ${colors.borderColor}`,
                  backgroundColor: 'transparent', color: colors.textSecondary,
                  fontSize: 12, fontWeight: 600, cursor: loadingMore ? 'wait' : 'pointer',
                }}
              >
                {loadingMore ? 'Carregando...' : 'Carregar mais'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// "+ Nova etapa" placeholder column
// ----------------------------------------------------------------------------
const CreateColumn = ({ colors, onCreate, atLimit, maxLabels, width = 300, openSignal = 0 }) => {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);

  // Botão "Nova etapa" do cabeçalho (2026-10-08): a coluna placeholder fica no
  // fim do scroll horizontal e o cliente achava que o recurso tinha sumido
  // (Gelo Americano, 6 de 20 etapas). O cabeçalho rola até aqui e abre o campo.
  useEffect(() => {
    if (!openSignal) return;
    if (!atLimit) setCreating(true);
    try {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' });
    } catch { /* noop */ }
  }, [openSignal, atLimit]);

  if (atLimit) {
    return (
      <div ref={rootRef} style={{
        width, flexShrink: 0, padding: 24,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', color: colors.textTertiary, fontSize: 12,
        border: `1px dashed ${colors.borderColor}`, borderRadius: 12,
      }}>
        Limite de {maxLabels} etapas atingido.
      </div>
    );
  }

  if (!creating) {
    return (
      <button
        ref={rootRef}
        onClick={() => setCreating(true)}
        style={{
          width, flexShrink: 0, padding: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 8, color: colors.textSecondary, fontSize: 14, fontWeight: 500,
          border: `2px dashed ${colors.borderColor}`, borderRadius: 12,
          backgroundColor: 'transparent', cursor: 'pointer',
          scrollSnapAlign: 'start',
          transition: 'border-color 150ms, color 150ms',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = colors.accentPrimary;
          e.currentTarget.style.color = colors.accentPrimary;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = colors.borderColor;
          e.currentTarget.style.color = colors.textSecondary;
        }}
      >
        <Plus size={18} /> Nova etapa
      </button>
    );
  }

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await onCreate(trimmed);
      setName('');
      setCreating(false);
    } catch (_) {
      // error handled upstream
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={rootRef} style={{
      width, flexShrink: 0, padding: 14,
      display: 'flex', flexDirection: 'column', gap: 10,
      border: `2px dashed ${colors.accentPrimary}`, borderRadius: 12,
      backgroundColor: colors.bgPrimary,
    }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
        Nome da etapa
      </span>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={50}
        placeholder="Ex.: Em negociação"
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') { setName(''); setCreating(false); }
        }}
        style={{
          padding: '10px 12px', borderRadius: 8,
          border: `1px solid ${colors.borderColor}`,
          backgroundColor: colors.bgSecondary,
          color: colors.textPrimary, fontSize: 14, outline: 'none',
        }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={submit}
          disabled={!name.trim() || busy}
          style={{
            flex: 1, padding: '8px 12px', borderRadius: 8,
            backgroundColor: colors.accentPrimary, color: 'white',
            border: 'none', fontSize: 13, fontWeight: 600,
            cursor: !name.trim() || busy ? 'not-allowed' : 'pointer',
            opacity: !name.trim() || busy ? 0.5 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          {busy ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={14} />}
          Criar
        </button>
        <button
          onClick={() => { setName(''); setCreating(false); }}
          style={{
            padding: '8px 12px', borderRadius: 8,
            backgroundColor: 'transparent', color: colors.textSecondary,
            border: `1px solid ${colors.borderColor}`,
            fontSize: 13, fontWeight: 500, cursor: 'pointer',
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Modal de nome (criar/renomear funil): substitui o window.prompt do browser.
// ----------------------------------------------------------------------------
const NamePromptModal = ({ prompt, colors, onClose }) => {
  const [name, setName] = useState(prompt?.initial || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(prompt?.initial || ''); setBusy(false); }, [prompt]);
  if (!prompt) return null;
  const submit = async () => {
    const v = name.trim();
    if (!v || busy) return;
    setBusy(true);
    try {
      await prompt.onConfirm(v);
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div style={{
        width: '100%', maxWidth: 380, borderRadius: 14, padding: 20,
        backgroundColor: colors.bgPrimary, border: `1px solid ${colors.borderColor}`,
        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>{prompt.title}</h3>
          {prompt.description && (
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: colors.textSecondary }}>{prompt.description}</p>
          )}
        </div>
        <input
          autoFocus
          value={name}
          maxLength={60}
          placeholder={prompt.placeholder || ''}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') onClose();
          }}
          style={{
            padding: '10px 12px', borderRadius: 8, fontSize: 14, outline: 'none',
            border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
            color: colors.textPrimary,
          }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${colors.borderColor}`, backgroundColor: 'transparent', color: colors.textSecondary,
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!name.trim() || busy}
            style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: 'none', backgroundColor: colors.accentPrimary, color: '#fff',
              cursor: !name.trim() || busy ? 'not-allowed' : 'pointer',
              opacity: !name.trim() || busy ? 0.6 : 1,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {busy && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            {prompt.confirmLabel || 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Skeleton do board (flag kanban_store): aparece no primeiro acesso da sessao,
// quando ainda nao ha board em cache para pintar. A revisita pinta o cache na
// hora e revalida por baixo, sem passar por aqui.
// ----------------------------------------------------------------------------
const BoardSkeleton = ({ colors, width, isMobile }) => (
  <div
    role="status"
    aria-label="Carregando funil..."
    style={{
      flex: 1, display: 'flex', gap: isMobile ? 10 : 14, overflow: 'hidden',
      alignItems: 'flex-start', paddingBottom: 8,
    }}
  >
    {[0, 1, 2, 3].map((i) => (
      <div
        key={i}
        style={{
          width, flexShrink: 0, padding: 12,
          backgroundColor: colors.bgPrimary,
          border: `1px solid ${colors.borderColor}`, borderRadius: 12,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}
      >
        <div
          className="kanban-skeleton-block"
          style={{ height: 14, width: `${58 - i * 9}%`, borderRadius: 6, backgroundColor: colors.bgTertiary }}
        />
        {[0, 1, 2].map((j) => (
          <div
            key={j}
            className="kanban-skeleton-block"
            style={{
              height: 58, borderRadius: 10, backgroundColor: colors.bgSecondary,
              animationDelay: `${(i + j) * 80}ms`,
            }}
          />
        ))}
      </div>
    ))}
  </div>
);

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------
const TagsKanbanPage = ({ onOpenChat }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { user, isCompanyMember, isCompanyAdmin } = useAuth();
  const currentUserId = user?.id;
  // Fase 4 (item B): membro de company com funis espelhados (shared_funnels)
  // vê os funis do admin em modo LEITURA (não gerencia definições) e cards de
  // outros membros abrem por delegação (detectado por card via c.origin).
  const [managedMember, setManagedMember] = useState(false);
  // Membro com funis compartilhados: "Automações da etapa" abre o resumo
  // somente leitura em vez da gaveta de edição.
  const [viewAutomationsLabel, setViewAutomationsLabel] = useState(null);
  // Exportação de contatos (CSV/XLSX) — modal com os mesmos filtros do drawer
  // do inbox (etapa, busca, campo personalizado). O filtro por campo já ativo
  // na tela entra pré-carregado no modal.
  const { flags } = useFeatureFlags();
  const exportOn = !!flags.contact_export;
  // C5 (2026-09-22), tres flags de GET /api/system/features. Ausente = false =
  // comportamento de sempre.
  //  - kanban_store: board em cache por funil (stale-while-revalidate), funis
  //    e board em paralelo, movimento do SSE aplicado no cliente, skeleton
  //    so no primeiro acesso da sessao.
  //  - kanban_delta (so faz efeito com kanban_store): a revalidacao apos um
  //    evento pede ao servidor apenas as colunas tocadas (delta_since).
  //  - kanban_cursor_paging: "Carregar mais" manda o `ts` do ultimo card em
  //    vez do offset.
  // Em ref para os callbacks estaveis (fetchAll, handleLoadMore, SSE) lerem o
  // valor corrente sem se recriarem.
  const kanbanStoreOn = Boolean(flags?.kanban_store);
  const kanbanDeltaOn = kanbanStoreOn && Boolean(flags?.kanban_delta);
  const cursorPagingOn = Boolean(flags?.kanban_cursor_paging);
  const kanbanFlagsRef = useRef({ store: false, delta: false, cursor: false });
  kanbanFlagsRef.current = { store: kanbanStoreOn, delta: kanbanDeltaOn, cursor: cursorPagingOn };
  const userIdRef = useRef(currentUserId);
  userIdRef.current = currentUserId;
  // Boot SINCRONO (kanban_store): se a flag ja e conhecida na montagem, o
  // ultimo funil aberto e o board dele (quando em cache) entram como estado
  // inicial, sem um frame de "Sem etapa" vazio nem de skeleton.
  const [boot] = useState(() => {
    if (!currentUserId || !getFeatureFlag('kanban_store')) return null;
    const fid = kanbanStore.getLastFunnelId(currentUserId);
    if (!fid) return null;
    return { funnelId: fid, board: kanbanStore.getBoard(currentUserId, fid) };
  });
  const [showExportModal, setShowExportModal] = useState(false);
  const handleExport = async ({ allFunnels, bucket, q, detail, format, customFieldValues, createdFrom, createdTo }) => {
    const { blob, filename, rows } = await authService.exportKanbanContacts({
      // "Todos os funis" (pedido do cliente 26/08): sem pipeline_id o backend
      // exporta todas as etapas de todos os funis do catálogo.
      pipelineId: allFunnels ? null : activeFunnelIdRef.current,
      bucket, q, detail, format,
      customFields: customFieldValues,
      createdFrom, createdTo,
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return { rows };
  };
  const [labels, setLabels] = useState(() => boot?.board?.labels || []);
  const [buckets, setBuckets] = useState(() => boot?.board?.buckets || {}); // { [labelId|"unlabeled"]: [contact, ...] }
  const [hasMore, setHasMore] = useState(() => boot?.board?.hasMore || {}); // { bucketId: bool } — paginação server-side
  const [counts, setCounts] = useState(() => boot?.board?.counts || {}); // { bucketId: total REAL } — count server-side (não a página)
  const [valueTotals, setValueTotals] = useState(() => boot?.board?.valueTotals || {}); // { bucketId: soma do campo Valor } — server-side
  // Limite e contagem TOTAL de etiquetas (todos os funis) vindos da API —
  // `labels` acima é só o funil ativo, então não serve para checar o limite.
  const [maxLabels, setMaxLabels] = useState(() => boot?.board?.maxLabels || DEFAULT_MAX_LABELS);
  // Contador que pede à coluna "+ Nova etapa" para rolar até ela e abrir o campo.
  const [createSignal, setCreateSignal] = useState(0);
  const [labelsTotal, setLabelsTotal] = useState(() => boot?.board?.labelsTotal || 0);
  // Responsividade mobile (mesmo padrão do Inbox): compacta header + faixa de funis.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [search, setSearch] = useState('');
  // #3: resultados da busca SERVER-SIDE (null = não buscando). Com >= 2 chars a
  // tela exibe estes buckets (varridos do banco) no lugar do board normal.
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  // Filtro por campo personalizado — MESMO hook e MESMOS controles do drawer
  // dos 4 inboxes. Sem ele, buscar "Civic" no funil devolvia zero dos 139
  // leads que têm Civic no campo MODELO (medido na myCar em 25/08).
  const campos = useCustomFieldFilter();
  const [showCamposPanel, setShowCamposPanel] = useState(false);
  // 409 required_fields ao mover: { move, outcomeReason, saleValue, detail } (03/09)
  const [requiredBlock, setRequiredBlock] = useState(null);
  // "Entrou no CRM entre" (data de criacao do contato) — vive no painel Campos.
  const [createdRange, setCreatedRange] = useState({ from: '', to: '' });
  const createdActive = Boolean(createdRange.from || createdRange.to);
  // Com board em cache (kanban_store) nao ha o que esperar: pinta e revalida.
  const [loading, setLoading] = useState(() => !boot?.board);
  const [error, setError] = useState(null);
  const [activeContact, setActiveContact] = useState(null);
  // Funis: a tela é multi-funil. Cada funil é um grupo de colunas (etapas,
  // labels no backend). Trocar de funil filtra as colunas exibidas.
  const [funnels, setFunnels] = useState([]);
  // kanban_store: comeca no ultimo funil aberto para o board carregar em
  // paralelo com a lista de funis (loadFunnels so troca se o id sumiu).
  const [activeFunnelId, setActiveFunnelId] = useState(() => boot?.funnelId || null);
  // Multi-funil (2026-09-16): o card mostra os OUTROS funis em que o lead
  // está. O board só traz as etiquetas do funil ativo; o catálogo inteiro
  // (cacheado no service) resolve as demais. Sem a flag, nada é buscado.
  const multiFunnel = Boolean(flags?.multi_funnel);
  const [catalogById, setCatalogById] = useState({});
  useEffect(() => {
    if (!multiFunnel) return undefined;
    let alive = true;
    authService.getLabels()
      .then((res) => {
        if (!alive) return;
        const list = res?.data || res || [];
        setCatalogById(Object.fromEntries((Array.isArray(list) ? list : []).map((l) => [l.id, l])));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [multiFunnel, labels]);
  const stageCtx = useMemo(() => ({
    multi: multiFunnel,
    catalogById,
    funnelsById: Object.fromEntries((funnels || []).map((f) => [f.id, f])),
    activeFunnelId,
  }), [multiFunnel, catalogById, funnels, activeFunnelId]);

  // Modal/drawer state: hold the FULL label object (id+name+color) so the
  // modal can render the colored swatch and pass label name to the backend
  // for the auto-generated campaign name.
  const [dispatchLabel, setDispatchLabel] = useState(null);
  // Atribuir responsável a todos os contatos de uma coluna (flag stage_bulk_assign).
  const [assignStage, setAssignStage] = useState(null);
  const stageBulkAssignOn = !!flags?.stage_bulk_assign;
  const [configLabel, setConfigLabel] = useState(null);
  // Movimentação aguardando o motivo do fechamento (caiu numa coluna de
  // ganho/perda). O card já andou na tela; o commit no servidor acontece
  // quando o operador responde ou pula o modal.
  const [pendingOutcome, setPendingOutcome] = useState(null);
  // Config de CRM da conta (cacheada no service): habilita o campo "Valor da
  // venda" no modal de ganho para contas com funil comercial (ex.: myCar).
  // Buscada no mount para o campo já existir quando o modal abrir.
  const [crmConfig, setCrmConfig] = useState(null);
  useEffect(() => {
    let alive = true;
    managerDashboardService.getCrmConfig().then((cfg) => {
      if (alive) setCrmConfig(cfg);
    });
    return () => { alive = false; };
  }, []);
  const [journeyContact, setJourneyContact] = useState(null);
  const [showFieldsConfig, setShowFieldsConfig] = useState(false);
  // Automação de entrada (coluna "Sem Etiqueta"): distribuição de novos
  // contatos para destino fixo ou round-robin entre membros/agentes.
  const [showEntryAutomation, setShowEntryAutomation] = useState(false);
  // Painel do admin: distribuição automática (round-robin) de contatos novos.
  const [showDistribution, setShowDistribution] = useState(false);
  // Painel do admin: config da roleta do botão de transferir (round-robin).
  const [showManualRoulette, setShowManualRoulette] = useState(false);
  // Régua de resposta (follow-up por inatividade) do funil ativo.
  const [showReplyRuler, setShowReplyRuler] = useState(false);
  const [replyRulers, setReplyRulers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    replyRulerService.list()
      .then((res) => { if (!cancelled) setReplyRulers(res?.data || []); })
      .catch(() => { if (!cancelled) setReplyRulers([]); });
    return () => { cancelled = true; };
  }, []);

  // Mouse: arrasta após 5px (ágil no desktop). Touch: só arrasta após long-press
  // de 200ms (tolerance:8 cancela se o dedo deslizar antes = trata como SCROLL).
  // Sem isso, um PointerSensor único sequestrava o scroll vertical da coluna como
  // arraste no celular (o cliente não conseguia rolar a fila nem mover o card).
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  // Flag levantada durante o optimistic drag-drop. SSE handler abaixo ignora
  // refetches enquanto isso para evitar o efeito de "card duplica visualmente
  // na coluna antiga e atualiza com delay" — que acontecia porque cada
  // operacao (remove + assign) emitia 1 SSE CONTACT_LABELS_CHANGED, gerando
  // 2 fetchAll() seguidos com estados intermediarios visiveis.
  const pendingOptimisticRef = useRef(false);
  const ssePendingFetchRef = useRef(null);
  // Funil ativo em ref para usos dentro de closures (SSE/revert) sem re-subscribe.
  const activeFunnelIdRef = useRef(null);
  useEffect(() => { activeFunnelIdRef.current = activeFunnelId; }, [activeFunnelId]);
  // Espelho dos buckets p/ o handleLoadMore ler o offset (nº já carregado) sem
  // recriar o callback a cada mudança de estado.
  const bucketsRef = useRef({});
  useEffect(() => { bucketsRef.current = buckets; }, [buckets]);
  // C5: espelho do board inteiro (o SSE aplica o movimento nele e o delta
  // funde por cima) e do funil que esta PINTADO na tela, que pode diferir do
  // ativo enquanto o board novo nao chegou. Com kanban_store, toda mudanca de
  // estado (drag, criar/apagar etapa, carregar mais) vai para o cache do
  // funil pintado, para a proxima visita ja nascer com ela.
  const boardRef = useRef({ labels, buckets, hasMore, counts, valueTotals, maxLabels, labelsTotal });
  const paintedFunnelIdRef = useRef(boot?.board ? boot.funnelId : null);
  useEffect(() => {
    boardRef.current = { labels, buckets, hasMore, counts, valueTotals, maxLabels, labelsTotal };
    if (!kanbanStoreOn || !paintedFunnelIdRef.current) return;
    kanbanStore.patchBoard(userIdRef.current, paintedFunnelIdRef.current, boardRef.current);
  }, [labels, buckets, hasMore, counts, valueTotals, maxLabels, labelsTotal, kanbanStoreOn]);

  // Pinta um board (do cache ou do servidor) e registra qual funil esta na tela.
  const paintBoard = useCallback((funnelId, board) => {
    paintedFunnelIdRef.current = funnelId;
    setLabels(board.labels || []);
    setBuckets(board.buckets || { [UNLABELED_KEY]: [] });
    setHasMore(board.hasMore || {});
    setCounts(board.counts || {});
    setValueTotals(board.valueTotals || {});
    if (board.maxLabels) setMaxLabels(board.maxLabels);
    setLabelsTotal(board.labelsTotal ?? (board.labels || []).length);
    setLoading(false);
  }, []);

  // `opts.delta`: pedido de revalidacao apos um evento. So vira delta_since de
  // verdade com kanban_store + kanban_delta e um carimbo conhecido do funil.
  const fetchAll = useCallback(async (funnelId, opts = {}) => {
    const { store: storeOn, delta: deltaOn } = kanbanFlagsRef.current;
    if (!storeOn) {
      // Caminho de sempre (sem kanban_store).
      setError(null);
      try {
        const res = await authService.getKanbanBoard(funnelId);
        const payload = res?.data || res;
        setLabels(payload?.labels || []);
        setBuckets(payload?.buckets || { [UNLABELED_KEY]: [] });
        setHasMore(payload?.has_more || {});
        setCounts(payload?.counts || {});
        setValueTotals(payload?.value_totals || {});
        if (payload?.max_labels) setMaxLabels(payload.max_labels);
        setLabelsTotal(payload?.labels_total ?? (payload?.labels || []).length);
      } catch (err) {
        console.error(err);
        setError('Não foi possível carregar as etapas do funil.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // kanban_store: stale-while-revalidate. Pinta o ultimo board conhecido do
    // funil na hora e busca o atual por baixo. Resposta que chega depois de o
    // operador trocar de funil e descartada (nao pinta nem mostra erro).
    const uid = userIdRef.current;
    const cached = kanbanStore.getBoard(uid, funnelId);
    if (cached && paintedFunnelIdRef.current !== funnelId) paintBoard(funnelId, cached);
    setError(null);
    const startedAt = new Date().toISOString();
    const useDelta = Boolean(
      opts.delta && deltaOn && cached && cached.deltaSince && paintedFunnelIdRef.current === funnelId,
    );
    try {
      let res = await authService.getKanbanBoard(funnelId, useDelta ? { deltaSince: cached.deltaSince } : {});
      if (activeFunnelIdRef.current !== funnelId) return;
      let payload = res?.data || res;
      let next;
      if (payload?.delta === true) {
        // So as colunas tocadas: funde por cima do que esta na tela. Sem base
        // (o funil pintado mudou no meio), pede o board inteiro.
        const base = paintedFunnelIdRef.current === funnelId
          ? { ...(cached || {}), ...boardRef.current }
          : null;
        if (base) {
          next = kanbanStore.mergeDelta(base, payload, startedAt);
        } else {
          res = await authService.getKanbanBoard(funnelId);
          if (activeFunnelIdRef.current !== funnelId) return;
          payload = res?.data || res;
          next = kanbanStore.boardFromPayload(payload, startedAt);
        }
      } else {
        next = kanbanStore.boardFromPayload(payload, startedAt);
      }
      kanbanStore.setBoard(uid, funnelId, next);
      paintBoard(funnelId, next);
    } catch (err) {
      if (activeFunnelIdRef.current !== funnelId) return;
      console.error(err);
      setError('Não foi possível carregar as etapas do funil.');
      setLoading(false);
    }
  }, [paintBoard]);

  // Carrega a próxima página de UMA coluna (botão "Carregar mais") — server-side.
  // kanban_cursor_paging: manda o `ts` do ultimo card como cursor (o offset
  // vai junto, para um backend sem cursor paginar como hoje). Card sem `ts`
  // cai no offset.
  const handleLoadMore = useCallback(async (bucketId) => {
    const carregados = bucketsRef.current[bucketId] || [];
    const offset = carregados.length;
    const ultimo = carregados[carregados.length - 1];
    const ultimoTs = ultimo ? Number(ultimo.ts) : NaN;
    const cursor = kanbanFlagsRef.current.cursor && Number.isFinite(ultimoTs) && ultimoTs > 0 ? ultimoTs : null;
    try {
      const res = await authService.getKanbanLabelPage(
        bucketId, activeFunnelIdRef.current, offset, 30, null, null, null,
        cursor !== null ? { cursor } : {},
      );
      const payload = res?.data || res;
      setBuckets((prev) => {
        const existing = prev[bucketId] || [];
        const seen = new Set(existing.map((c) => cardKey(c)));
        const fresh = (payload?.contacts || []).filter((c) => !seen.has(cardKey(c)));
        return { ...prev, [bucketId]: [...existing, ...fresh] };
      });
      setHasMore((prev) => ({ ...prev, [bucketId]: !!payload?.has_more }));
    } catch (e) {
      console.error('[Kanban] load more', e);
    }
  }, []);

  // Carrega os funis (cria default na 1ª vez) e seleciona o primeiro.
  const loadFunnels = useCallback(async () => {
    try {
      const list = await authService.listFunnels();
      setFunnels(list);
      setActiveFunnelId((prev) => (prev && list.some(f => f.id === prev) ? prev : (list[0]?.id || null)));
    } catch (err) {
      console.error('listFunnels error', err);
    }
  }, []);

  useEffect(() => { loadFunnels(); }, [loadFunnels]);
  useEffect(() => { if (activeFunnelId) fetchAll(activeFunnelId); }, [activeFunnelId, fetchAll]);
  // kanban_store: se a flag so chegou depois da montagem, o ultimo funil
  // aberto entra agora e o board dele carrega em paralelo com a lista de
  // funis (loadFunnels mantem o id se ele ainda existir). E o funil ativo
  // fica lembrado para a proxima visita.
  useEffect(() => {
    if (!kanbanStoreOn) return;
    setActiveFunnelId((prev) => prev || kanbanStore.getLastFunnelId(userIdRef.current) || null);
  }, [kanbanStoreOn]);
  useEffect(() => {
    if (kanbanStoreOn && activeFunnelId) kanbanStore.setLastFunnelId(userIdRef.current, activeFunnelId);
  }, [kanbanStoreOn, activeFunnelId]);

  // #3: busca SERVER-SIDE com debounce — varre TODOS os contatos no banco (igual
  // ao chatlist), não só a página carregada. < 2 chars: limpa e volta ao board.
  useEffect(() => {
    const q = search.trim();
    // Filtro por campo SEM termo digitado é busca válida: o operador quer o
    // funil inteiro restrito a "MODELO contém Civic". Só volta ao board
    // completo quando não há nem termo nem filtro.
    if (q.length < 2 && !campos.active && !createdActive) {
      setSearchResults(null); setSearchLoading(false); return undefined;
    }
    let alive = true;
    setSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await authService.searchKanban(
          q, activeFunnelIdRef.current, campos.values,
          createdRange.from || null, createdRange.to || null);
        const payload = res?.data || res;
        if (alive) setSearchResults(payload?.buckets || {});
      } catch (e) {
        console.error('[Kanban] search', e);
        if (alive) setSearchResults({});
      } finally {
        if (alive) setSearchLoading(false);
      }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [search, activeFunnelId, campos.active, campos.values, createdRange]);

  // Descobre se este viewer é um membro com funis espelhados (modo leitura).
  useEffect(() => {
    let alive = true;
    if (!isCompanyMember) { setManagedMember(false); return undefined; }
    companyService.getMyCompany()
      .then((res) => {
        if (alive) setManagedMember(res?.company?.settings?.shared_funnels_enabled === true);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [isCompanyMember]);

  // Card de outro membro (visão "all"): entra no workspace de origem por
  // delegação (autorizado pelo funnel_access="all"; escopo do contato) e
  // recarrega como o alvo — o padrão da Etapa 2 (WorkspacesGrid).
  const handleEnterForeign = useCallback(async (contact) => {
    const ownerId = contact?.origin?.user_id;
    if (!ownerId) return;
    try {
      // Escopo do contato pela chave do cartao: telefone no WhatsApp, chave
      // opaca intacta no Instagram e no Messenger (phone vem nulo nesses).
      const data = await companyService.enterWorkspace(ownerId, cardKey(contact) || null);
      startDelegation(data);
      // Destino pelo canal do cartao (2026-09-15, mesma familia do bug do
      // Kanban embutido corrigido em v5-5-122): IG/MS caiam no inbox do
      // WhatsApp. O pedido de abrir a conversa sobrevive ao reload em
      // sessionStorage.pendingOpenChat, lido pelos 4 shells.
      const ref = String(contact?.contact_ref || contact?.phone || '');
      let rota = '/inbox';
      let pendente = { contact_phone: contact?.phone || null };
      if (ref.startsWith('instagram:') || contact?.channel === 'instagram') {
        rota = '/instagram';
        pendente = { contact_igsid: ref.replace(/^instagram:/, '') };
      } else if (ref.startsWith('messenger:') || contact?.channel === 'messenger') {
        rota = '/messenger';
        pendente = { contact_psid: ref.replace(/^messenger:/, '') };
      } else if (ref.startsWith('widget:') || contact?.channel === 'widget') {
        rota = '/widget';
        pendente = { contact_wid: ref.replace(/^widget:/, '') };
      } else if (contact?.channel === 'wa_business') {
        rota = '/wa-business';
      }
      try { sessionStorage.setItem('pendingOpenChat', JSON.stringify(pendente)); } catch (_e) { /* storage */ }
      window.location.href = rota;
    } catch (err) {
      const parsed = companyService.parseCompanyError(err);
      setError(parsed.message || 'Não foi possível abrir este contato.');
    }
  }, []);

  // Real-time: refetch on contact_labels_changed.
  // - Skip enquanto ha um drag-drop optimistic em andamento (evita
  //   estado intermediario do meio dos 2 SSEs).
  // - Debounce de 350ms: coalesce eventos em rajada (ex: bulk import
  //   gera N eventos seguidos).
  // Evento de OUTRO membro (fanout company-wide, Bloco B): agrupa por 3 s e
  // nao adia um refetch ja agendado. Numa empresa com 15 membros e automacao
  // movendo etapa o tempo todo, refazer o board a cada evento pesaria para todos.
  // Com kanban_store + kanban_delta o refetch pede so as colunas tocadas;
  // sem elas e o board inteiro, como sempre foi.
  const scheduleRevalidate = useCallback((fanout) => {
    const run = () => {
      ssePendingFetchRef.current = null;
      fetchAll(activeFunnelIdRef.current, { delta: true });
    };
    if (fanout) {
      if (ssePendingFetchRef.current) return;
      ssePendingFetchRef.current = setTimeout(run, 3000);
      return;
    }
    if (ssePendingFetchRef.current) clearTimeout(ssePendingFetchRef.current);
    ssePendingFetchRef.current = setTimeout(run, 350);
  }, [fetchAll]);

  useSSEEvent(SSE_EVENTS.CONTACT_LABELS_CHANGED, (data) => {
    if (pendingOptimisticRef.current) return;
    if (kanbanFlagsRef.current.store && paintedFunnelIdRef.current
        && paintedFunnelIdRef.current === activeFunnelIdRef.current) {
      // kanban_store: o card anda na tela na hora (coluna, contagem e valor);
      // a revalidacao abaixo confirma. Card que nao esta carregado (contato
      // novo ou fora da pagina) fica so com a revalidacao.
      const next = kanbanStore.applyLabelsChange(boardRef.current, data, multiFunnel ? catalogById : null);
      if (next) {
        boardRef.current = { ...boardRef.current, buckets: next.buckets, counts: next.counts, valueTotals: next.valueTotals };
        setBuckets(next.buckets);
        setCounts(next.counts);
        setValueTotals(next.valueTotals);
      }
    }
    scheduleRevalidate(!!data?.fanout);
  });

  // Move semantics: dragging removes the source label and adds the target label.
  // Dragging from "Sem Etiqueta" only adds; dragging to "Sem Etiqueta" only removes
  // the source label. Other labels on the contact are preserved.
  const applyMove = (contact, sourceColumnId, targetColumnId) => {
    const next = {};
    Object.entries(buckets).forEach(([key, list]) => {
      next[key] = list.slice();
    });

    const newLabelIds = new Set(contact.label_ids || []);
    if (sourceColumnId && sourceColumnId !== UNLABELED_KEY) {
      newLabelIds.delete(sourceColumnId);
    }
    if (targetColumnId !== UNLABELED_KEY) {
      newLabelIds.add(targetColumnId);
    }
    const updated = { ...contact, label_ids: Array.from(newLabelIds) };

    const key = cardKey(contact);
    // Remove the card from the source column
    if (sourceColumnId) {
      next[sourceColumnId] = (next[sourceColumnId] || []).filter(
        (c) => cardKey(c) !== key
      );
    }

    // Refresh label_ids on every other entry of this contact still on the board
    Object.keys(next).forEach((k) => {
      next[k] = next[k].map((c) =>
        cardKey(c) === key ? updated : c
      );
    });

    if (targetColumnId === UNLABELED_KEY) {
      // Only land in "Sem Etiqueta" if the contact has no labels left
      if (newLabelIds.size === 0) {
        next[UNLABELED_KEY] = next[UNLABELED_KEY] || [];
        if (!next[UNLABELED_KEY].some((c) => cardKey(c) === key)) {
          next[UNLABELED_KEY] = [updated, ...next[UNLABELED_KEY]];
        }
      }
    } else {
      next[targetColumnId] = next[targetColumnId] || [];
      if (!next[targetColumnId].some((c) => cardKey(c) === key)) {
        next[targetColumnId] = [updated, ...next[targetColumnId]];
      }
      // It now has a label, so it can no longer be in "Sem Etiqueta"
      next[UNLABELED_KEY] = (next[UNLABELED_KEY] || []).filter(
        (c) => cardKey(c) !== key
      );
    }

    setBuckets(next);
    // kanban_store: contagem e valor das colunas acompanham o card na hora
    // (sem a flag, como sempre, so mudam no proximo refetch).
    if (kanbanFlagsRef.current.store) {
      const t = kanbanStore.retotal(buckets, next, updated, counts, valueTotals);
      setCounts(t.counts);
      setValueTotals(t.valueTotals);
    }
  };

  // Desfaz o ajuste de contagem/valor do drag optimistic (so com kanban_store).
  const revertTotals = (move) => {
    if (!kanbanFlagsRef.current.store || !move?.previousCounts) return;
    setCounts(move.previousCounts);
    setValueTotals(move.previousValueTotals || {});
  };

  const handleDragStart = (event) => {
    const contact = event.active?.data?.current?.contact;
    if (contact) setActiveContact(contact);
  };

  const handleDragEnd = async (event) => {
    setActiveContact(null);
    const { active, over } = event;
    if (!over) return;

    const contact = active.data?.current?.contact;
    const sourceColumnId = active.data?.current?.sourceColumnId;
    if (!contact || !sourceColumnId) return;

    const targetId = over.id;
    if (targetId === sourceColumnId) return;

    // Contato de OUTRO workspace (visão company-wide): a etiqueta é gravada na
    // partição do DONO. O servidor revalida se o caller (admin/membro com
    // acesso ao funil) pode mover.
    const ownerUserId = (contact.origin && contact.origin.user_id && contact.origin.user_id !== currentUserId)
      ? contact.origin.user_id
      : undefined;

    const previousBuckets = buckets;
    applyMove(contact, sourceColumnId, targetId);
    const move = {
      contact, sourceColumnId, targetId, ownerUserId, previousBuckets,
      // Snapshot para o revert com kanban_store (ignorado sem a flag).
      previousCounts: counts, previousValueTotals: valueTotals,
    };

    // Marca optimistic em andamento: o handler de SSE
    // CONTACT_LABELS_CHANGED checa essa flag e ignora os refetches
    // intermediarios (cada remove/assign emite 1 evento, sao 2 no total).
    // Sem isso o card "duplica visualmente" entre as colunas com delay.
    pendingOptimisticRef.current = true;

    // Coluna que FECHA o funil (ganho/perda): pergunta o motivo ANTES de
    // gravar, para o assign já levar o texto e não precisar de 2ª escrita. O
    // card fica onde o operador soltou; a flag acima segura o SSE enquanto o
    // modal está aberto, senão um refetch desfaria o movimento na tela.
    const targetLabel = labels.find((l) => l.id === targetId);
    if (targetLabel && (targetLabel.outcome === 'won' || targetLabel.outcome === 'lost')) {
      setPendingOutcome({ ...move, label: targetLabel });
      return;
    }

    await commitMove(move);
  };

  /**
   * Grava a movimentação no servidor. Separado do handleDragEnd porque, numa
   * coluna de ganho/perda, só roda depois que o operador responde (ou pula) o
   * modal de motivo. `outcomeReason` é ignorado pelo servidor fora dessas
   * etapas.
   */
  const commitMove = async (move, outcomeReason, saleValue, outcomeAt) => {
    if (!move) return;
    const { contact, sourceColumnId, targetId, ownerUserId, previousBuckets } = move;
    pendingOptimisticRef.current = true;
    try {
      // SEQUENCIAL (nao paralelo). Antes era Promise.all([remove, assign]) e
      // o backend gravava o evento na ordem que cada request chegasse — na
      // pratica o assign frequentemente era logado ANTES do remove,
      // bagunçando a timeline ("entrou em B" antes de "saiu de A").
      // Sequencial garante ordem cronologica: primeiro saiu, depois entrou.
      if (sourceColumnId !== UNLABELED_KEY) {
        await authService.removeLabelFromContact(cardKey(contact), sourceColumnId, ownerUserId);
      }
      if (targetId !== UNLABELED_KEY && !(contact.label_ids || []).includes(targetId)) {
        await authService.assignLabelToContact(cardKey(contact), targetId, ownerUserId, outcomeReason, outcomeAt);
      }
      // Valor da venda digitado no modal de ganho: best-effort DEPOIS do move
      // (falha aqui não desfaz a etiqueta — o valor dá para preencher no drawer).
      if (saleValue != null && crmConfig?.value_field) {
        customFieldsService
          .setContactFields(cardKey(contact), { [crmConfig.value_field]: saleValue })
          .catch((err) => console.warn('[Kanban] valor da venda não salvo:', err));
      }
      // kanban_delta: os SSEs do proprio movimento foram ignorados durante o
      // optimistic; uma revalidacao barata (so as colunas tocadas) alinha a
      // ordem e os totais com o servidor.
      if (kanbanFlagsRef.current.delta) scheduleRevalidate(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === 'required_fields') {
        // Campos obrigatorios da etapa de destino (ou nome so com telefone):
        // volta o card e abre o formulario; "Salvar e mover" repete o move.
        setBuckets(previousBuckets);
        revertTotals(move);
        setRequiredBlock({ move, outcomeReason, saleValue, detail });
        return;
      }
      console.error(err);
      setError('Não foi possível mover o contato. Atualizando...');
      setBuckets(previousBuckets);
      revertTotals(move);
      fetchAll(activeFunnelIdRef.current);
    } finally {
      pendingOptimisticRef.current = false;
    }
  };

  // Criar/renomear funil: modal próprio (era window.prompt do browser).
  const [namePrompt, setNamePrompt] = useState(null);

  const handleCreateFunnel = () => {
    setNamePrompt({
      title: 'Novo funil',
      description: 'Cada funil tem as próprias etapas, automações e régua.',
      placeholder: 'Ex.: Vendas, Pós-venda, Suporte',
      confirmLabel: 'Criar funil',
      onConfirm: async (name) => {
        try {
          const f = await authService.createFunnel(name);
          await loadFunnels();
          setActiveFunnelId(f.id);
        } catch (err) {
          setError(err?.response?.data?.detail || 'Não foi possível criar o funil.');
        }
      },
    });
  };

  const handleRenameFunnel = (funnel) => {
    setNamePrompt({
      title: 'Renomear funil',
      initial: funnel.name,
      confirmLabel: 'Salvar',
      onConfirm: async (name) => {
        if (name === funnel.name) return;
        try {
          await authService.updateFunnel(funnel.id, name);
          await loadFunnels();
        } catch (err) {
          setError(err?.response?.data?.detail || 'Não foi possível renomear o funil.');
        }
      },
    });
  };

  // Escolha única: marcar um funil como padrão desmarca os outros no backend.
  const handleSetDefaultFunnel = async (funnel) => {
    if (funnel.is_default) return;
    if (!window.confirm(`Tornar "${funnel.name}" o funil padrão? Contatos novos e a automação de entrada passam a usar este funil.`)) return;
    try {
      await authService.setDefaultFunnel(funnel.id);
      await loadFunnels();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Não foi possível definir o funil padrão.');
    }
  };

  const handleDeleteFunnel = async (funnel) => {
    if (funnel.is_default) return;
    if (!window.confirm(`Excluir o funil "${funnel.name}"? As etapas dele voltam para o funil padrão.`)) return;
    try {
      await authService.deleteFunnel(funnel.id);
      if (kanbanFlagsRef.current.store) kanbanStore.removeBoard(userIdRef.current, funnel.id);
      setActiveFunnelId(null);
      await loadFunnels();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Não foi possível excluir o funil.');
    }
  };

  const handleCreateLabel = async (name) => {
    try {
      // Cria a coluna DENTRO do funil ativo.
      const res = await authService.createLabel(name, pickColor(labels.length), activeFunnelIdRef.current);
      const newLabel = res?.data || res;
      setLabels((prev) => [...prev, newLabel]);
      setLabelsTotal((prev) => prev + 1);
      setBuckets((prev) => ({ ...prev, [newLabel.id]: [] }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Erro ao criar etapa.');
      throw err;
    }
  };

  const handleRenameLabel = async (labelId, newName) => {
    try {
      await authService.updateLabel(labelId, { name: newName });
      setLabels((prev) => prev.map((l) => (l.id === labelId ? { ...l, name: newName } : l)));
    } catch (err) {
      setError('Erro ao renomear etapa.');
      console.error(err);
    }
  };

  const handleChangeLabelColor = async (labelId, newColor) => {
    const previous = labels;
    setLabels((prev) => prev.map((l) => (l.id === labelId ? { ...l, color: newColor } : l)));
    try {
      await authService.updateLabel(labelId, { color: newColor });
    } catch (err) {
      setLabels(previous);
      setError('Erro ao atualizar a cor.');
      console.error(err);
    }
  };

  // Reordena as colunas do funil ativo: troca a coluna idx com a vizinha
  // (dir -1 esquerda, +1 direita), atualiza local e persiste a nova ordem.
  const handleMoveColumn = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= labels.length) return;
    const next = [...labels];
    [next[idx], next[target]] = [next[target], next[idx]];
    setLabels(next);
    try {
      await authService.reorderLabels(activeFunnelIdRef.current, next.map((l) => l.id));
    } catch (err) {
      setError('Não foi possível reordenar as colunas.');
      fetchAll(activeFunnelIdRef.current);
    }
  };

  const handleDeleteLabel = async (labelId) => {
    if (!window.confirm('Excluir esta etapa? Os contatos dela voltam para "Sem etapa".')) return;
    try {
      await authService.deleteLabel(labelId);
      setLabels((prev) => prev.filter((l) => l.id !== labelId));
      setLabelsTotal((prev) => Math.max(0, prev - 1));
      setBuckets((prev) => {
        const moved = prev[labelId] || [];
        const next = { ...prev };
        delete next[labelId];
        // Move contacts that had only this label back to unlabeled
        const additions = moved
          .filter((c) => (c.label_ids || []).filter((id) => id !== labelId).length === 0)
          .map((c) => ({ ...c, label_ids: [] }));
        next[UNLABELED_KEY] = [...additions, ...(next[UNLABELED_KEY] || [])];
        return next;
      });
    } catch (err) {
      setError('Erro ao excluir etapa.');
      console.error(err);
    }
  };

  // #3: busca SERVER-SIDE. Com >= 2 chars exibimos os buckets vindos do servidor
  // (varredura do banco inteiro); senão, o board paginado normal. Antes isto era
  // um filtro CLIENT-SIDE que só achava o que já estava carregado na 1ª página.
  const isSearching = search.trim().length >= 2 || campos.active || createdActive;
  const camposBtnActive = campos.active || createdActive;
  const filteredBuckets = useMemo(
    () => (isSearching ? (searchResults || {}) : buckets),
    [isSearching, searchResults, buckets],
  );
  // Count no header da coluna: total REAL do servidor (não a página carregada,
  // que era o bug do "30 → 60"). Durante a busca, mostra quantos cards casaram
  // na página já carregada (não dá pra contar o resto sem buscar tudo).
  const columnCount = (key) =>
    isSearching
      ? (filteredBuckets[key] || []).length
      : (counts[key] ?? (buckets[key] || []).length);

  const columnWidth = isMobile ? 'min(300px, calc(100vw - 56px))' : 300;

  const styles = {
    container: {
      flex: 1, display: 'flex', flexDirection: 'column',
      backgroundColor: colors.bgSecondary, overflow: 'hidden',
      minWidth: 0,
      // Mobile: os shells só reservam as barras fixas da NavSidebar (54 em
      // cima, 58 embaixo) para a lista de conversas; as outras páginas
      // precisam reservar sozinhas, senão o header nasce atrás da barra.
      height: isMobile ? '100dvh' : '100vh',
      boxSizing: 'border-box',
      paddingTop: isMobile ? navMobileTop() : 0,
      paddingBottom: isMobile ? navMobileBottom() : 0,
    },
    // App bar no MESMO padrão das outras telas (ícone quadrado + título +
    // subtítulo à esquerda, ações à direita). Sem z-index: os popovers desta
    // tela usam position fixed (ActionMenu) ou z-index próprio, então o header
    // não precisa virar contexto de empilhamento.
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: isMobile ? '8px 12px' : '10px 24px', backgroundColor: colors.bgPrimary,
      minHeight: isMobile ? 0 : 60,
      flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? 8 : 12,
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
    headerIcon: {
      width: 40, height: 40, backgroundColor: colors.accentPrimary,
      borderRadius: 10, display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: 'white', flexShrink: 0,
    },
    title: { fontSize: 18, fontWeight: 600, color: colors.textPrimary, margin: 0 },
    subtitle: {
      fontSize: 13, color: colors.textSecondary, margin: 0,
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
    headerRight: {
      display: 'flex', alignItems: 'center', gap: 8,
      width: isMobile ? '100%' : 'auto', minWidth: 0,
    },
    searchBar: {
      display: 'flex', alignItems: 'center', gap: 6,
      backgroundColor: colors.bgTertiary, borderRadius: 20,
      padding: '0 12px', height: 38,
      width: isMobile ? undefined : 280, flex: isMobile ? 1 : undefined,
      minWidth: 0,
    },
    searchInput: {
      flex: 1, minWidth: 0, padding: '8px 4px', border: 'none', background: 'transparent',
      fontSize: 14, color: colors.textPrimary, outline: 'none',
    },
    // Botão secundário do funil: pílula preenchida, a MESMA linguagem da
    // busca ao lado e dos filtros do inbox (bgTertiary, sem borda aparente).
    // Antes era retângulo vazado e destoava da busca e do inbox. Filtrar,
    // Exportar, Importar e Configurar funil saem todos daqui.
    ghostBtn: {
      display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
      height: 38, padding: '0 16px', borderRadius: 19, fontSize: 13, fontWeight: 600,
      border: '1px solid transparent', backgroundColor: colors.bgTertiary,
      color: colors.textSecondary, cursor: 'pointer', whiteSpace: 'nowrap',
      transition: 'background-color 120ms, color 120ms, border-color 120ms',
    },
    // Faixa de funis: abas à esquerda (rolam em X quando não cabem) e UM
    // botão de configuração do funil à direita, com as automações dentro.
    funnelStrip: {
      display: 'flex', alignItems: 'center', gap: 8,
      padding: isMobile ? '6px 10px' : '6px 16px',
      backgroundColor: colors.bgPrimary,
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    funnelTabs: {
      display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0,
      overflowX: 'auto', scrollbarWidth: 'none',
    },
    boardArea: {
      flex: 1, padding: isMobile ? '12px 12px 8px' : 20, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    },
    board: {
      flex: 1, display: 'flex', gap: isMobile ? 10 : 14, overflowX: 'auto', paddingBottom: 8,
      alignItems: 'flex-start',
      // Mobile: uma coluna por "página" de rolagem.
      scrollSnapType: isMobile ? 'x mandatory' : 'none',
      scrollPaddingLeft: isMobile ? 12 : 0,
    },
  };

  const funnelTab = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
    height: 32, padding: '0 12px', borderRadius: 8, border: 'none',
    cursor: 'pointer', whiteSpace: 'nowrap',
    backgroundColor: active ? `${colors.accentPrimary}15` : 'transparent',
    color: active ? colors.accentPrimary : colors.textSecondary,
    fontSize: 13, fontWeight: active ? 700 : 600,
    transition: 'background-color 120ms, color 120ms',
  });

  const activeFunnel = funnels.find((f) => f.id === activeFunnelId) || null;
  // Régua ligada no funil ativo: sinaliza no botão de configuração e no menu.
  const rulerOn = replyRulers.some((r) => r.pipeline_id === activeFunnelId && r.enabled);

  // Menu "Configurar funil": automações e configurações que antes eram quatro
  // botões soltos na faixa (Distribuição, Roleta, Régua, Configurar campos).
  const funnelMenuItems = [
    { section: 'Automações do funil' },
    ...(!managedMember ? [{
      key: 'entry', label: 'Automação de entrada', icon: Inbox,
      hint: 'Contato novo no funil: distribuir e disparar',
      onClick: () => setShowEntryAutomation(true),
    }] : []),
    {
      key: 'ruler', label: 'Régua de resposta', icon: Timer, active: rulerOn,
      hint: rulerOn ? 'Ligada neste funil' : 'Follow-up de quem parou de responder',
      onClick: () => setShowReplyRuler(true),
    },
    {
      key: 'distribution', label: 'Distribuição automática', icon: Shuffle,
      hint: 'Rodízio de contatos novos entre a equipe',
      onClick: () => setShowDistribution(true),
    },
    {
      key: 'roulette', label: 'Roleta de transferência', icon: Users,
      hint: 'Grupos do botão Transferir',
      onClick: () => setShowManualRoulette(true),
    },
    { section: 'Configurações' },
    {
      key: 'fields', label: 'Campos personalizados', icon: SlidersHorizontal,
      hint: 'Abas e campos da ficha do contato',
      onClick: () => setShowFieldsConfig(true),
    },
    ...(!managedMember && activeFunnel ? [
      { key: 'rename', label: 'Renomear funil', icon: Pencil, onClick: () => handleRenameFunnel(activeFunnel) },
      {
        key: 'default', label: 'Tornar funil padrão', icon: Crown,
        disabled: !!activeFunnel.is_default,
        hint: activeFunnel.is_default ? 'Este já é o funil padrão' : 'Só um funil pode ser o padrão',
        onClick: () => handleSetDefaultFunnel(activeFunnel),
      },
      {
        key: 'delete', label: 'Excluir funil', icon: Trash2, danger: true,
        disabled: !!activeFunnel.is_default,
        hint: activeFunnel.is_default ? 'O funil padrão não pode ser excluído' : undefined,
        onClick: () => handleDeleteFunnel(activeFunnel),
      },
    ] : []),
  ];

  // Ações da página que no mobile saem do header e entram num menu "⋯".
  const pageMenuItems = [
    {
      key: 'import', label: 'Importar contatos', icon: UploadCloud,
      hint: 'CSV ou planilha', onClick: () => navigate('/contacts/import'),
    },
    ...(exportOn ? [{
      key: 'export', label: 'Exportar contatos', icon: Download,
      hint: 'CSV ou XLSX', onClick: () => setShowExportModal(true),
    }] : []),
  ];

  const totalContacts = isSearching
    ? Object.values(filteredBuckets).reduce((sum, list) => sum + list.length, 0)
    : (Object.keys(counts).length
        ? Object.values(counts).reduce((sum, n) => sum + (n || 0), 0)
        : Object.values(buckets).reduce((sum, list) => sum + list.length, 0));

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerIcon}>
            <Kanban size={20} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <h1 style={styles.title}>Funis</h1>
            {!isMobile && (
              <p style={styles.subtitle}>
                Arraste os contatos entre as etapas · {totalContacts} contatos · {labelsTotal}/{maxLabels} etapas
              </p>
            )}
          </div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.searchBar}>
            <Search size={16} color={colors.textTertiary} />
            <input
              type="text"
              placeholder="Buscar contato..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={styles.searchInput}
            />
            {searchLoading && (
              <Loader2
                size={14}
                style={{ animation: 'spin 1s linear infinite', color: colors.textTertiary }}
              />
            )}
          </div>
          {campos.fields.length > 0 && (
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => setShowCamposPanel((v) => !v)}
                title="Filtrar por campo personalizado"
                style={{
                  ...styles.ghostBtn,
                  padding: isMobile ? 0 : '0 16px', width: isMobile ? 38 : undefined,
                  justifyContent: 'center',
                  border: `1px solid ${camposBtnActive ? colors.accentPrimary : 'transparent'}`,
                  backgroundColor: camposBtnActive ? `${colors.accentPrimary}15` : colors.bgTertiary,
                  color: camposBtnActive ? colors.accentPrimary : colors.textSecondary,
                }}
              >
                <SlidersHorizontal size={15} />
                {!isMobile && 'Filtrar'}
                {camposBtnActive && (
                  <span style={{
                    minWidth: 18, height: 18, borderRadius: 9,
                    backgroundColor: colors.accentPrimary, color: '#fff',
                    fontSize: 11, fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 5px',
                    position: isMobile ? 'absolute' : 'static',
                    top: -6, right: -6,
                  }}>
                    {campos.values.length + (createdActive ? 1 : 0)}
                  </span>
                )}
              </button>
              {showCamposPanel && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 300,
                  width: 'min(300px, calc(100vw - 24px))', maxHeight: 420, overflowY: 'auto',
                  padding: 14, borderRadius: 12,
                  backgroundColor: colors.bgPrimary,
                  border: `1px solid ${colors.borderColor}`,
                  boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: 10,
                  }}>
                    <strong style={{ fontSize: 13, color: colors.textPrimary }}>
                      Filtrar por campo
                    </strong>
                    <button
                      type="button"
                      onClick={() => { campos.clear(); setCreatedRange({ from: '', to: '' }); setShowCamposPanel(false); }}
                      style={{
                        background: 'transparent', border: 'none', padding: 0,
                        color: colors.textTertiary, fontSize: 11, cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      limpar
                    </button>
                  </div>
                  <p style={{ margin: '0 0 10px', fontSize: 11, color: colors.textSecondary, lineHeight: 1.45 }}>
                    Agrupa todos os leads com o mesmo valor, em todas as etapas.
                  </p>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary, marginBottom: 6 }}>
                      Entrou no CRM entre
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="date"
                        aria-label="Entrou no CRM a partir de"
                        value={createdRange.from}
                        max={createdRange.to || undefined}
                        onChange={(e) => setCreatedRange((r) => ({ ...r, from: e.target.value || '' }))}
                        style={{ flex: 1, minWidth: 0, padding: '6px 8px', borderRadius: 8, border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary, color: colors.textPrimary, fontSize: 12 }}
                      />
                      <span style={{ fontSize: 11, color: colors.textTertiary }}>até</span>
                      <input
                        type="date"
                        aria-label="Entrou no CRM até"
                        value={createdRange.to}
                        min={createdRange.from || undefined}
                        onChange={(e) => setCreatedRange((r) => ({ ...r, to: e.target.value || '' }))}
                        style={{ flex: 1, minWidth: 0, padding: '6px 8px', borderRadius: 8, border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary, color: colors.textPrimary, fontSize: 12 }}
                      />
                    </div>
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: colors.textSecondary, lineHeight: 1.45 }}>
                      Data em que o contato foi criado no CRM. Contato migrado conta a data da migração.
                    </p>
                  </div>
                  <CustomFieldFilterFields
                    fields={campos.fields}
                    values={campos.values}
                    onChange={campos.setValues}
                    colors={colors}
                  />
                </div>
              )}
            </div>
          )}
          {!managedMember && (
            <button
              type="button"
              onClick={() => setCreateSignal((n) => n + 1)}
              disabled={labelsTotal >= maxLabels}
              title={labelsTotal >= maxLabels
                ? `Limite de ${maxLabels} etapas atingido`
                : 'Criar uma etapa no fim do funil'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, height: 38,
                padding: isMobile ? '0 10px' : '0 14px', borderRadius: 19, border: 'none',
                backgroundColor: colors.accentPrimary, color: '#fff', fontSize: 13,
                fontWeight: 600, cursor: labelsTotal >= maxLabels ? 'not-allowed' : 'pointer',
                opacity: labelsTotal >= maxLabels ? 0.5 : 1, whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              <Plus size={16} />
              {!isMobile && 'Nova etapa'}
            </button>
          )}
          {isMobile ? (
            <ActionMenu
              colors={colors}
              items={pageMenuItems}
              trigger={({ open }) => (
                <IconButton
                  icon={MoreHorizontal} label="Mais ações" colors={colors}
                  size={38} iconSize={18} active={open}
                  style={{ border: `1px solid ${colors.borderColor}` }}
                />
              )}
            />
          ) : (
            <>
              {exportOn && (
                <button
                  type="button"
                  onClick={() => setShowExportModal(true)}
                  title="Exportar contatos do funil (CSV/XLSX)"
                  style={styles.ghostBtn}
                >
                  <Download size={15} />
                  Exportar
                </button>
              )}
              {/* Atalho para importar contatos por CSV/XLSX (Fase 3.2): quem
                  está organizando o funil tende a precisar do import logo. */}
              <button
                type="button"
                onClick={() => navigate('/contacts/import')}
                title="Importar contatos via CSV ou planilha"
                style={styles.ghostBtn}
              >
                <UploadCloud size={15} />
                Importar contatos
              </button>
            </>
          )}
        </div>
      </div>

      {/* Faixa de FUNIS: abas (uma por funil) + "Novo funil" + menu único
          "Configurar funil" com as automações e configurações do funil ativo. */}
      <div style={styles.funnelStrip}>
        <div style={styles.funnelTabs} className="funnel-tabs">
          {funnels.map((f) => {
            const active = f.id === activeFunnelId;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setActiveFunnelId(f.id)}
                onDoubleClick={() => !managedMember && handleRenameFunnel(f)}
                title={f.is_default ? 'Funil padrão' : f.name}
                style={funnelTab(active)}
              >
                {f.is_default && <Crown size={12} style={{ opacity: active ? 1 : 0.7 }} />}
                {f.name}
              </button>
            );
          })}
          {!managedMember && (
            <button
              type="button"
              onClick={handleCreateFunnel}
              title="Criar novo funil"
              style={{ ...funnelTab(false), color: colors.textTertiary, paddingLeft: 8, paddingRight: 10 }}
            >
              <Plus size={14} /> Novo funil
            </button>
          )}
        </div>
        <ActionMenu
          colors={colors}
          items={funnelMenuItems}
          title={activeFunnel ? `Funil ${activeFunnel.name}` : 'Funil'}
          width={272}
          trigger={({ open }) => (
            <button
              type="button"
              title="Automações e configurações do funil"
              style={{
                ...styles.ghostBtn, height: 32, borderRadius: 16,
                padding: isMobile ? 0 : '0 14px', width: isMobile ? 32 : undefined,
                justifyContent: 'center', position: 'relative',
                color: open ? colors.accentPrimary : colors.textSecondary,
                borderColor: open ? colors.accentPrimary : 'transparent',
                backgroundColor: open ? `${colors.accentPrimary}15` : colors.bgTertiary,
              }}
            >
              <Settings size={15} />
              {!isMobile && 'Configurar funil'}
              {rulerOn && (
                <span
                  title="Régua de resposta ligada neste funil"
                  style={{
                    position: 'absolute', top: -3, right: -3, width: 9, height: 9,
                    borderRadius: 5, backgroundColor: '#22c55e',
                    border: `2px solid ${colors.bgPrimary}`,
                  }}
                />
              )}
            </button>
          )}
        />
      </div>

      <div style={styles.boardArea}>
        {error && (
          <div style={{
            backgroundColor: '#fee2e2', color: '#991b1b',
            padding: '10px 14px', borderRadius: 8, marginBottom: 12,
            fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span>{error}</span>
            <button onClick={() => setError(null)} style={{
              background: 'transparent', border: 'none', color: '#991b1b', cursor: 'pointer',
            }}>
              <X size={16} />
            </button>
          </div>
        )}

        {loading ? (
          kanbanStoreOn ? (
            <BoardSkeleton colors={colors} width={columnWidth} isMobile={isMobile} />
          ) : (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: colors.textTertiary, gap: 8,
            }}>
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
              Carregando funil...
            </div>
          )
        ) : (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveContact(null)}
          >
            <div style={styles.board}>
              {/* "Sem Etiqueta" first */}
              <KanbanColumn
                id={UNLABELED_KEY}
                title="Sem etapa"
                color={colors.textTertiary}
                count={columnCount(UNLABELED_KEY)}
                contacts={filteredBuckets[UNLABELED_KEY] || []}
                colors={colors}
                isUnlabeled
                onOpenChat={onOpenChat}
                onOpenJourney={setJourneyContact}
                onConfig={() => setShowEntryAutomation(true)}
                onAssignAll={stageBulkAssignOn ? () => setAssignStage({ id: UNLABELED_KEY, name: 'Sem etapa', count: columnCount(UNLABELED_KEY), isUnlabeled: true }) : undefined}
                readOnly={managedMember}
                currentUserId={currentUserId}
                onEnterForeign={handleEnterForeign}
                viewerIsAdmin={isCompanyAdmin}
                hasMore={!isSearching && !!hasMore[UNLABELED_KEY]}
                onLoadMore={handleLoadMore}
                funnelId={activeFunnelId}
                width={columnWidth}
                stageCtx={stageCtx}
              />

              {labels.map((label, idx) => (
                <KanbanColumn
                  key={label.id}
                  id={label.id}
                  title={label.name}
                  color={label.color}
                  count={columnCount(label.id)}
                  contacts={filteredBuckets[label.id] || []}
                  colors={colors}
                  onRename={(newName) => handleRenameLabel(label.id, newName)}
                  onDelete={() => handleDeleteLabel(label.id)}
                  onChangeColor={(newColor) => handleChangeLabelColor(label.id, newColor)}
                  onOpenChat={onOpenChat}
                  onDispatch={() => setDispatchLabel(label)}
                  onConfig={() => (managedMember ? setViewAutomationsLabel(label) : setConfigLabel(label))}
                  onAssignAll={stageBulkAssignOn ? () => setAssignStage({ id: label.id, name: label.name, count: columnCount(label.id), isUnlabeled: false }) : undefined}
                  onOpenJourney={setJourneyContact}
                  canMoveLeft={idx > 0}
                  canMoveRight={idx < labels.length - 1}
                  onMove={(dir) => handleMoveColumn(idx, dir)}
                  readOnly={managedMember}
                  currentUserId={currentUserId}
                  onEnterForeign={handleEnterForeign}
                  viewerIsAdmin={isCompanyAdmin}
                  hasMore={!isSearching && !!hasMore[label.id]}
                  onLoadMore={handleLoadMore}
                  funnelId={activeFunnelId}
                  outcome={label.outcome}
                  valueTotal={valueTotals[label.id] || 0}
                  width={columnWidth}
                  stageCtx={stageCtx}
                />
              ))}

              {!managedMember && (
                <CreateColumn
                  colors={colors}
                  onCreate={handleCreateLabel}
                  atLimit={labelsTotal >= maxLabels}
                  maxLabels={maxLabels}
                  width={columnWidth}
                  openSignal={createSignal}
                />
              )}
            </div>

            <DragOverlay>
              {activeContact ? (
                <ContactCard contact={activeContact} colors={colors} isOverlay stageCtx={stageCtx} />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Fase 1: campaign dispatch drawer — reuses ScheduleMessageModal em
          modo label_broadcast no mesmo padrao visual (drawer lateral 720px)
          dos drawers de adicionar followup e configurar automacao da
          etiqueta. */}
      <StageAssignModal
        isOpen={!!assignStage}
        onClose={() => setAssignStage(null)}
        stage={assignStage}
        pipelineId={activeFunnelId}
        colors={colors}
        onDone={() => { fetchAll(activeFunnelIdRef.current); }}
      />
      <ScheduleMessageModal
        isOpen={!!dispatchLabel}
        onClose={() => setDispatchLabel(null)}
        mode="label_broadcast"
        labelInfo={dispatchLabel}
        colors={colors}
        displayMode="drawer"
      />

      {/* Automação de entrada do funil (drawer unificado, igual ao das
          etiquetas): o que disparar + distribuir + regras. Coluna "Sem
          Etiqueta" = contatos novos que chegam no funil. */}
      <LabelConfigDrawer
        mode="entry"
        funnel={funnels.find((f) => f.id === activeFunnelId) || null}
        isOpen={showEntryAutomation}
        onClose={() => setShowEntryAutomation(false)}
        colors={colors}
      />

      {/* Régua de resposta do funil ativo (follow-up por inatividade). */}
      <ReplyRulerDrawer
        isOpen={showReplyRuler}
        onClose={() => setShowReplyRuler(false)}
        funnel={funnels.find((f) => f.id === activeFunnelId) || null}
        funnelLabels={labels}
        colors={colors}
        onSaved={(doc) => {
          if (!doc) return;
          setReplyRulers((prev) => {
            const rest = prev.filter((r) => r.id !== doc.id);
            return [...rest, doc];
          });
        }}
      />

      {/* Painel do admin: distribuição automática (round-robin) de novos contatos. */}
      <DistributionStatsDrawer
        isOpen={showDistribution}
        onClose={() => setShowDistribution(false)}
        colors={colors}
      />

      {/* Motivo do fechamento: aparece ao soltar um card numa coluna marcada
          como ganho/perda. Pular move o contato do mesmo jeito. */}
      <OutcomeReasonModal
        isOpen={!!pendingOutcome}
        colors={colors}
        outcome={pendingOutcome?.label?.outcome}
        labelName={pendingOutcome?.label?.name}
        contactName={pendingOutcome?.contact?.display_name}
        options={pendingOutcome?.label?.outcome_reason_options || []}
        askValue={!!crmConfig?.value_field}
        askDate={!!crmConfig?.outcome_date}
        onConfirm={(reason, extra) => {
          const move = pendingOutcome;
          setPendingOutcome(null);
          commitMove(move, reason, extra?.saleValue, extra?.outcomeAt);
        }}
        onSkip={(extra) => {
          const move = pendingOutcome;
          setPendingOutcome(null);
          commitMove(move, undefined, extra?.saleValue, extra?.outcomeAt);
        }}
      />

      {/* Config da roleta do botão de transferir (admin). */}
      <ManualTransferRouletteDrawer
        isOpen={showManualRoulette}
        onClose={() => setShowManualRoulette(false)}
        colors={colors}
      />

      <StageAutomationsSummary
        isOpen={!!viewAutomationsLabel}
        onClose={() => setViewAutomationsLabel(null)}
        label={viewAutomationsLabel}
        colors={colors}
        funnelLabels={labels}
      />

      {/* Fase 2: per-label config drawer (auto_dispatch + rules). */}
      <LabelConfigDrawer
        isOpen={!!configLabel}
        onClose={() => setConfigLabel(null)}
        label={configLabel}
        colors={colors}
        funnelLabels={labels}
        onSaved={(updatedLabel) => {
          setLabels((prev) => prev.map((l) => {
            if (l.id === updatedLabel.id) return { ...l, ...updatedLabel };
            // Ganho/perda é exclusivo por funil e o backend TRANSFERE a
            // marcação. `labels` já é só o funil ativo, então quem tinha esse
            // resultado acabou de perdê-lo — sem isto o selo antigo ficaria
            // fantasma até o próximo reload do board.
            if (updatedLabel.outcome && l.outcome === updatedLabel.outcome) {
              return { ...l, outcome: null };
            }
            return l;
          }));
        }}
      />

      {/* PR-B: per-contact journey drawer with Logs + Graph tabs. */}
      <ContactJourneyDrawer
        isOpen={!!journeyContact}
        onClose={() => setJourneyContact(null)}
        contact={journeyContact}
        colors={colors}
      />

      {/* Campos personalizados: admin do schema (abas + campos tipados). */}
      <CustomFieldsConfigDrawer
        isOpen={showFieldsConfig}
        onClose={() => setShowFieldsConfig(false)}
        colors={colors}
      />

      <NamePromptModal prompt={namePrompt} colors={colors} onClose={() => setNamePrompt(null)} />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .funnel-tabs::-webkit-scrollbar { display: none; }
        @keyframes kanbanPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
        .kanban-skeleton-block { animation: kanbanPulse 1.2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .kanban-skeleton-block { animation: none; opacity: 0.7; }
        }
      `}</style>
      {requiredBlock && (
        <RequiredFieldsBlockModal
          colors={colors}
          phone={cardKey(requiredBlock.move.contact)}
          detail={requiredBlock.detail}
          onClose={() => setRequiredBlock(null)}
          onRetry={async () => {
            const { move, outcomeReason, saleValue, outcomeAt } = requiredBlock;
            setRequiredBlock(null);
            await commitMove({ ...move, previousBuckets: bucketsRef.current }, outcomeReason, saleValue, outcomeAt);
          }}
        />
      )}
      <ExportContactsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        colors={colors}
        funnelName={(funnels.find((f) => f.id === activeFunnelId)?.name) || 'Funil'}
        labels={labels}
        customFields={campos.fields}
        initialCustomFieldValues={campos.values}
        onExport={handleExport}
      />

    </div>
  );
};

export default TagsKanbanPage;
